import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '../../ui/card';
import MembersDirectoryTable from './members/MembersDirectoryTable';
import MemberEditModal from './members/MemberEditModal';
import MemberHardDeleteModal from './members/MemberHardDeleteModal';
import MembersOutreachSection from './members/MembersOutreachSection';
import MemberRemoveModal from './members/MemberRemoveModal';
import MemberRolesModal from './members/MemberRolesModal';
import MembersToolbar from './members/MembersToolbar';
import AddMemberModal from './members/AddMemberModal';
import {
  DAD_STAGE_JOIN_PRIORITY,
  DAD_STAGE_OPTIONS,
  getDadRelationshipStage,
} from './members/memberConstants';
import { useDadOutreachWorkflow } from './members/useDadOutreachWorkflow';
import { useAddMemberForm } from './members/useAddMemberForm';

const getJoinModeStageTone = (value) => {
  if (value === 'dad_partners_founding_forty_considering') return 'primary';
  if (value === 'dad_partners_founding_forty_joined') return 'primary';
  if (value === 'prospect' || value === 'webinar_attended') return 'secondary';
  if (value === 'webinar_registered' || value === 'webinar_invited') return 'muted';
  return 'neutral';
};

const getStageFilterButtonClass = ({ value, isActive, joinFirstMode }) => {
  if (!joinFirstMode || value === 'all') {
    return isActive
      ? 'border-aqua bg-aqua text-charcoal shadow-[0_10px_30px_-18px_rgba(91,181,162,0.75)]'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300 dark:hover:bg-[#202028]';
  }

  const tone = getJoinModeStageTone(value);

  if (isActive) {
    if (tone === 'primary') {
      return 'border-orange-300 bg-gradient-to-r from-orange-300 to-amber-200 text-[#1f140a] shadow-[0_16px_34px_-22px_rgba(249,115,22,0.8)] dark:border-orange-400/60 dark:bg-gradient-to-r dark:from-orange-400/80 dark:to-amber-300/70 dark:text-[#22170d]';
    }
    if (tone === 'secondary') {
      return 'border-cyan-300 bg-cyan-200 text-[#0d2022] shadow-[0_16px_34px_-22px_rgba(34,211,238,0.65)] dark:border-cyan-300/50 dark:bg-cyan-300/70 dark:text-[#0d2022]';
    }
    if (tone === 'muted') {
      return 'border-slate-300 bg-slate-200 text-slate-900 shadow-[0_12px_28px_-20px_rgba(100,116,139,0.45)] dark:border-slate-300/30 dark:bg-slate-300/25 dark:text-slate-100';
    }
    return 'border-aqua bg-aqua text-charcoal shadow-[0_10px_30px_-18px_rgba(91,181,162,0.75)]';
  }

  if (tone === 'primary') {
    return 'border-orange-200 bg-orange-50/95 text-orange-800 hover:bg-orange-100 dark:border-orange-500/25 dark:bg-orange-500/10 dark:text-orange-300 dark:hover:bg-orange-500/15';
  }
  if (tone === 'secondary') {
    return 'border-cyan-200 bg-cyan-50/95 text-cyan-800 hover:bg-cyan-100 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300 dark:hover:bg-cyan-500/15';
  }
  if (tone === 'muted') {
    return 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-[#2a2a32] dark:bg-[#13161b] dark:text-slate-400 dark:hover:bg-[#1a1e26]';
  }
  return 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300 dark:hover:bg-[#202028]';
};

const matchesStageFilter = (member, stageFilter) => {
  if (stageFilter === 'all') return true;
  const relationshipStage = getDadRelationshipStage(member);
  if (stageFilter === 'member') {
    return relationshipStage === 'member';
  }
  return relationshipStage === stageFilter;
};

const isDadRecord = (member) => {
  const tier = (member.tier || '').trim().toLowerCase();
  return tier !== 'guide_partner';
};

const matchesTierFilter = (member, tierFilter) => {
  if (tierFilter === 'all') return true;
  return (member.tier || '').trim().toLowerCase() === tierFilter;
};

const getRoomDefaultTierFilter = (activeRoomId, fallback = 'all') => {
  if (activeRoomId === 'support-dads') return 'member';
  if (activeRoomId === 'reach-dads') return 'prospect';
  return fallback;
};

const MembersTab = ({
  adminFetch,
  searchTerm = '',
  setSearchTerm = () => {},
  members = [],
  loading = false,
  showRolesModal,
  setShowRolesModal,
  showRemoveModal,
  setShowRemoveModal,
  memberToRemove,
  setMemberToRemove,
  removeReason,
  setRemoveReason,
  removing,
  showHardDeleteModal,
  setShowHardDeleteModal,
  memberToHardDelete,
  setMemberToHardDelete,
  hardDeleting,
  openHardDeleteModal,
  handleHardDeleteMember,
  reinstatingId,
  handleReinstateMember,
  selectedMember,
  newMember,
  setNewMember,
  newMemberPhotoFile,
  setNewMemberPhotoFile,
  adding,
  savingRoles,
  memberRoles,
  setMemberRoles,
  showRemoved,
  setShowRemoved,
  showEditModal,
  setShowEditModal,
  editingMember,
  editMember,
  setEditMember,
  savingEdit,
  uploadingPhoto,
  editMemberPhotoInputRef,
  openEditModal,
  handleEditMemberPhotoUpload,
  openRolesModal,
  toggleAdditionalRole,
  handleSaveRoles,
  handleAddMember,
  importingDads,
  handleImportDads,
  openRemoveModal,
  handleRemoveMember,
  handleSaveMemberEdit,
  fetchMembers,
  founding40PublicMode = 'join',
  defaultTierFilter = 'all',
  activeZoneId = null,
  activeRoomId = null,
}) => {
  const joinFirstMode = founding40PublicMode !== 'webinar';
  const effectiveDefaultTierFilter = getRoomDefaultTierFilter(activeRoomId, defaultTierFilter);
  const [tierFilter, setTierFilter] = useState(effectiveDefaultTierFilter);
  const [stageFilter, setStageFilter] = useState('all');
  const addMemberForm = useAddMemberForm({ adminFetch, fetchMembers });

  useEffect(() => {
    setTierFilter(effectiveDefaultTierFilter);
  }, [activeRoomId, activeZoneId, effectiveDefaultTierFilter]);
  const displayedStageOptions = useMemo(() => {
    if (!joinFirstMode) return DAD_STAGE_OPTIONS;
    return [...DAD_STAGE_OPTIONS].sort((a, b) => {
      const left = DAD_STAGE_JOIN_PRIORITY[a.value] ?? 99;
      const right = DAD_STAGE_JOIN_PRIORITY[b.value] ?? 99;
      return left - right;
    });
  }, [joinFirstMode]);

  const dadMembers = useMemo(
    () => (Array.isArray(members) ? members : []).filter(
      (m) => tierFilter === 'guide_partner' || isDadRecord(m),
    ),
    [members, tierFilter],
  );

  const filteredMembers = dadMembers
    .filter(m => m.name?.toLowerCase().includes(searchTerm.toLowerCase()) || m.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(m => matchesTierFilter(m, tierFilter))
    .filter(m => {
      const status = m.status;
      if (showRemoved) {
        if (status != null && status !== undefined && !['former', 'trial', 'active'].includes(status)) return false;
      } else {
        if (status != null && status !== undefined && status !== 'active') return false;
      }
      return true;
    })
    .filter(m => matchesStageFilter(m, stageFilter));

  const stageCounts = useMemo(() => {
    const visibleMembers = dadMembers
      .filter((member) => matchesTierFilter(member, tierFilter))
      .filter((member) => {
        const status = member.status;
        if (showRemoved) return status == null || status === undefined || ['former', 'trial', 'active'].includes(status);
        return status == null || status === undefined || status === 'active';
      });

    return DAD_STAGE_OPTIONS.reduce((counts, option) => {
      counts[option.value] = visibleMembers.filter((member) => matchesStageFilter(member, option.value)).length;
      return counts;
    }, {});
  }, [dadMembers, showRemoved, tierFilter]);

  const outreachWorkflow = useDadOutreachWorkflow({
    adminFetch,
    fetchMembers,
    handleImportDads,
    importingDads,
    joinFirstMode,
    dadMembers,
    filteredMembers,
  });
  const {
    selectedDadIds,
    toggleDadSelection,
    openDadImportModal,
    openWebinarImportModal,
  } = outreachWorkflow;

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-aqua animate-spin" />
        </div>
      ) : (
        <>
          <MembersToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            tierFilter={tierFilter}
            setTierFilter={setTierFilter}
            onOpenDadImport={openDadImportModal}
            onOpenWebinarImport={openWebinarImportModal}
            onOpenAddMember={addMemberForm.openAddModal}
            joinFirstMode={joinFirstMode}
            displayedStageOptions={displayedStageOptions}
            stageFilter={stageFilter}
            setStageFilter={setStageFilter}
            stageCounts={stageCounts}
            showRemoved={showRemoved}
            setShowRemoved={setShowRemoved}
            getStageFilterButtonClass={getStageFilterButtonClass}
          />

          <MembersOutreachSection
            joinFirstMode={joinFirstMode}
            workflow={outreachWorkflow}
          />

          <AddMemberModal
            open={addMemberForm.showAddModal}
            newMember={addMemberForm.newMember}
            setNewMember={addMemberForm.setNewMember}
            newMemberPhotoFile={addMemberForm.newMemberPhotoFile}
            setNewMemberPhotoFile={addMemberForm.setNewMemberPhotoFile}
            adding={addMemberForm.adding}
            onSubmit={addMemberForm.handleAddMember}
            onClose={addMemberForm.closeAddModal}
          />

          <MemberEditModal
            show={showEditModal}
            editingMember={editingMember}
            editMember={editMember}
            setEditMember={setEditMember}
            savingEdit={savingEdit}
            uploadingPhoto={uploadingPhoto}
            editMemberPhotoInputRef={editMemberPhotoInputRef}
            handleEditMemberPhotoUpload={handleEditMemberPhotoUpload}
            handleSaveMemberEdit={handleSaveMemberEdit}
            onClose={() => setShowEditModal(false)}
          />

          <MembersDirectoryTable
            filteredMembers={filteredMembers}
            showRemoved={showRemoved}
            selectedDadIds={selectedDadIds}
            toggleDadSelection={toggleDadSelection}
            openEditModal={openEditModal}
            openRolesModal={openRolesModal}
            openRemoveModal={openRemoveModal}
            handleReinstateMember={handleReinstateMember}
            reinstatingId={reinstatingId}
            openHardDeleteModal={openHardDeleteModal}
          />

          <MemberRolesModal
            show={showRolesModal}
            selectedMember={selectedMember}
            memberRoles={memberRoles}
            setMemberRoles={setMemberRoles}
            toggleAdditionalRole={toggleAdditionalRole}
            savingRoles={savingRoles}
            handleSaveRoles={handleSaveRoles}
            onClose={() => setShowRolesModal(false)}
          />

          <MemberRemoveModal
            show={showRemoveModal}
            memberToRemove={memberToRemove}
            removeReason={removeReason}
            setRemoveReason={setRemoveReason}
            removing={removing}
            handleRemoveMember={handleRemoveMember}
            onClose={() => {
              setShowRemoveModal(false);
              setMemberToRemove(null);
              setRemoveReason('');
            }}
          />

          <MemberHardDeleteModal
            show={showHardDeleteModal}
            memberToHardDelete={memberToHardDelete}
            hardDeleting={hardDeleting}
            handleHardDeleteMember={handleHardDeleteMember}
            onClose={() => {
              setShowHardDeleteModal(false);
              setMemberToHardDelete(null);
            }}
          />
        </>
      )}
    </div>
  );
};

export default MembersTab;
